import { useEffect, useMemo, useState } from 'react';
import { PopulationScreen } from '../screens/Population/PopulationScreen';
import * as endpoints from '../api/endpoints';
import type { GroupByResult } from '../api/types';
import { theme } from '../../design-system/theme/theme';
import { formatGroupValue } from '../utils/formatValue';

/**
 * 집계 뷰어 컨테이너
 *
 * 실제 groupby API 연동: GET /data/{schema}/{table}/groupby
 * - 차트 x축 = 서버 기본 primary_group(날짜 절삭). 1차 그룹(pri) 지정 시 그 컬럼이 x축.
 * - 그룹 기준 3단계(pri/sec/ter) — 전부 선택(없음 가능). 1차 없음 = 날짜 기본.
 * - 좌: 스택 막대(1차 기준 x, 2차 기준 세그먼트) / 우: 카테고리 컬럼별 값 카운트 필터 패널.
 * - 하단: groupby 결과 표.
 *
 * category(저카디널리티) 컬럼은 서버가 알려주지 않으므로 테이블별로 큐레이션.
 * dateCol/serverDate 은 실 API 확인값. (comments는 기본 primary 없음 → 날짜 명시 필요)
 */

// 데이터 시각화 팔레트 (tokens.ts chart). 스택 세그먼트/필터 점 색으로 사용.
const PALETTE = [
  theme.chart.donut.correct,
  theme.chart.series1,
  theme.chart.gauge,
  theme.chart.donut.warning,
  theme.chart.donut.error,
  theme.chart.donut.fatal,
];

interface TableCat {
  label: string;
  /** 서버에 테이블 기본 primary_group(날짜 절삭)이 있으면 true. */
  serverDate: boolean;
  /** serverDate=false일 때 1차로 명시할 날짜 컬럼. */
  dateCol?: string;
  /** 그룹/필터에 쓸 category(저카디널리티) 실제 컬럼. */
  categories: string[];
}

// 컬럼 확인: GET /data/{module}/{table}/schema, 값 분포는 groupby 로 확인.
const CATALOG: Record<string, Record<string, TableCat>> = {
  naverdiscuss: {
    posts: {
      label: '게시글',
      serverDate: true,
      categories: ['stock_name', 'is_domestic', 'is_stock', 'is_index'],
    },
    comments: {
      label: '댓글',
      serverDate: false,
      dateCol: 'date',
      categories: [],
    },
  },
  reddit: {
    posts: {
      label: '게시글',
      serverDate: true,
      categories: ['subreddit', 'post_type', 'post_flair'],
    },
    comments: {
      label: '댓글',
      serverDate: false,
      dateCol: 'created',
      categories: ['depth'],
    },
  },
  kosown: {
    td_mkt_stck_dcby_inf: {
      label: 'td_mkt_stck_dcby_inf',
      serverDate: true,
      categories: [],
    },
    // 아래 4개도 백엔드 PRIMARY_GROUP 에 기본 날짜 컬럼이 설정돼 있음(라이브 검증 완료).
    // → serverDate:true 로 기본 날짜버킷 x축 + 기간(from/to) 필터 사용.
    tb_krx_new_pban: {
      label: 'tb_krx_new_pban',
      serverDate: true,
      categories: ['pbl_bur_nm', 'lstn_dv_cd'],
    },
    tb_etf_cmpf_inf: {
      label: 'tb_etf_cmpf_inf',
      serverDate: true,
      categories: ['cptco_cd'],
    },
    tb_equity_daily_data: {
      label: 'tb_equity_daily_data',
      serverDate: true,
      categories: ['market', 'country', 'currency', 'data_frequency'],
    },
    tb_kid_mn_etf_pban: {
      label: 'tb_kid_mn_etf_pban',
      serverDate: true,
      categories: ['pban_rpot_dv_cd', 'subm_bur_nm'],
    },
  },
};

const MODULE_OPTIONS = Object.keys(CATALOG);
// 기본(날짜 버킷) 집계가 주 용도라 그룹 수가 많지 않음 — 200이면 충분.
// comments의 raw 타임스탬프처럼 그룹이 많은 케이스는 200에서 잘림(minor).
const MAX_ROWS = 3000;

/** 서버 rows 값(불리언/숫자/null 혼재)을 표시용 문자열로 정규화. */
function normalizeResult(res: GroupByResult): GroupByResult {
  return {
    ...res,
    rows: res.rows.map((r) => ({
      ...r,
      values: r.values.map(formatGroupValue),
    })),
  };
}

/** 집계 뷰어 컨테이너: 실제 groupby API로 차트·표·필터 패널 구성. */
export function PopulationContainer() {
  const [module, setModule] = useState<string>(MODULE_OPTIONS[0]);
  const tableKeys = Object.keys(CATALOG[module]);
  const [table, setTable] = useState<string>(tableKeys[0]);
  // 그룹 기준 2단계 [pri, sec]. 전부 선택(빈 값 = 없음).
  const [levels, setLevels] = useState<string[]>(['', '']);
  // 기간 필터(yyyy-MM-dd, 빈 값 = 없음). 테이블 기본 날짜 컬럼이 있는 경우만 사용.
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');

  const cat = CATALOG[module][table];

  const [main, setMain] = useState<GroupByResult | null>(null);
  const [facetsRaw, setFacetsRaw] = useState<{ title: string; items: { label: string; count: number }[] }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tableOptions = tableKeys.map((k) => ({
    label: CATALOG[module][k].label,
    value: k,
  }));

  const handleChangeModule = (m: string) => {
    setModule(m);
    setTable(Object.keys(CATALOG[m])[0]);
    setLevels(['', '']);
    setFrom('');
    setTo('');
  };

  const handleChangeTable = (t: string) => {
    setTable(t);
    setLevels(['', '']);
    setFrom('');
    setTo('');
  };

  const handleClearRange = () => {
    setFrom('');
    setTo('');
  };

  // 단계 변경. sec는 뒤에서 compact되므로 개별 변경만 반영.
  const handleChangeLevel = (index: number, value: string) => {
    setLevels((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // 요청 컬럼: pri = 1차(없으면 서버 기본 날짜 / comments는 dateCol 명시).
  // sec = 2차(빈 칸이면 미전송).
  const pri = levels[0] || (cat.serverDate ? undefined : cat.dateCol);
  const sec = levels[1] || undefined;
  const cols = useMemo(
    () => ({ pri, sec }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [module, table, levels[0], levels[1]]
  );

  // 기간 필터는 서버가 테이블 기본 날짜 컬럼 기준으로만 지원 → serverDate 테이블만 허용.
  const rangeSupported = cat.serverDate;
  const range = useMemo(
    () => (rangeSupported ? { from: from || undefined, to: to || undefined } : undefined),
    [rangeSupported, from, to]
  );

  // 메인 집계: 차트 + 표.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    endpoints
      .fetchGroupBy(module, table, cols, MAX_ROWS, 0, range)
      .then((res) => {
        if (alive) setMain(normalizeResult(res));
      })
      .catch((e: Error) => {
        if (alive) {
          setMain(null);
          setError(e.message);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [module, table, cols, range]);

  // 필터 패널: category 컬럼별 값 카운트 (각 gcol_pri=col 단일 호출).
  useEffect(() => {
    let alive = true;
    const catCols = CATALOG[module][table].categories;
    if (catCols.length === 0) {
      setFacetsRaw([]);
      return;
    }
    Promise.all(
      catCols.map((col) =>
        endpoints
          .fetchGroupBy(module, table, { pri: col }, MAX_ROWS, 0, range)
          .then((res) => ({
            title: col,
            items: res.rows
              .map((r) => ({ label: formatGroupValue(r.values[0]), count: r.count }))
              .sort((a, b) => b.count - a.count),
          }))
          .catch(() => ({ title: col, items: [] as { label: string; count: number }[] }))
      )
    ).then((all) => {
      if (alive) setFacetsRaw(all);
    });
    return () => {
      alive = false;
    };
  }, [module, table, range]);

  // 1차 그룹 미지정 = x축이 기본 날짜 버킷 → 최근이 왼쪽/위로 오게 desc 정렬.
  const dateDefault = !levels[0];

  // 메인 응답 → 차트/표/색 파생.
  const derived = useMemo(() => {
    if (!main || main.rows.length === 0) {
      return {
        chartData: [] as { label: string; segments: { key: string; value: number }[] }[],
        legend: [] as { color: string; label: string }[],
        colorMap: {} as Record<string, string>,
        groupColumns: main?.groupColumns ?? [],
        groupRows: [] as (string | number)[][],
        totalCount: 0,
      };
    }
    const gc = main.groupColumns;
    const stacked = gc.length >= 2;

    // x(1번째 그룹) → 세그먼트(2번째 그룹) → count 합. 3번째 그룹은 세그먼트로 합산.
    const xMap = new Map<string, Map<string, number>>();
    const segOrder: string[] = [];
    const segSeen = new Set<string>();
    main.rows.forEach((r) => {
      const x = r.values[0] ?? '(null)';
      const seg = stacked ? r.values[1] ?? '(null)' : '전체';
      if (!xMap.has(x)) xMap.set(x, new Map());
      const m = xMap.get(x)!;
      m.set(seg, (m.get(seg) ?? 0) + r.count);
      if (!segSeen.has(seg)) {
        segSeen.add(seg);
        segOrder.push(seg);
      }
    });

    const colorMap: Record<string, string> = {};
    segOrder.forEach((s, i) => {
      colorMap[s] = PALETTE[i % PALETTE.length];
    });

    // 기본 날짜 축이면 desc(최근 왼쪽), 그 외에는 asc.
    const dir = dateDefault ? -1 : 1;
    const chartData = Array.from(xMap.entries())
      .sort((a, b) => dir * a[0].localeCompare(b[0]))
      .map(([label, m]) => ({
        label,
        segments: segOrder.map((k) => ({ key: k, value: m.get(k) ?? 0 })),
      }));

    const legend = stacked ? segOrder.map((k) => ({ color: colorMap[k], label: k })) : [];
    // 표도 동일 기준: 기본 날짜 축이면 첫 컬럼(날짜) desc(최근 위).
    const sortedRows = dateDefault
      ? [...main.rows].sort((a, b) => -(a.values[0] ?? '').localeCompare(b.values[0] ?? ''))
      : main.rows;
    const groupRows = sortedRows.map((r) => [...r.values, r.count] as (string | number)[]);
    const totalCount = main.rows.reduce((s, r) => s + r.count, 0);

    return { chartData, legend, colorMap, groupColumns: gc, groupRows, totalCount };
  }, [main, dateDefault]);

  const colorOf = (key: string) => derived.colorMap[key] ?? theme.color.primary;

  // 필터 패널: 2차 그룹(스택 기준)과 같은 컬럼이면 세그먼트 색 점 부여.
  const stackDim = derived.groupColumns[1];
  const facets = facetsRaw.map((f) => ({
    title: f.title,
    items: f.items.map((it) => ({
      label: it.label,
      count: it.count,
      color: f.title === stackDim ? colorOf(it.label) : undefined,
    })),
  }));

  const xDim = derived.groupColumns[0] ?? (cat.serverDate ? '날짜' : cat.dateCol ?? '날짜');
  const chartTitle = `${xDim}별 population${stackDim ? ` · ${stackDim} 스택` : ''}`;

  return (
    <PopulationScreen
      moduleOptions={MODULE_OPTIONS}
      module={module}
      onChangeModule={handleChangeModule}
      tableOptions={tableOptions}
      table={table}
      onChangeTable={handleChangeTable}
      dimensionOptions={cat.categories}
      levels={levels}
      onChangeLevel={handleChangeLevel}
      rangeSupported={rangeSupported}
      from={from}
      to={to}
      onChangeFrom={setFrom}
      onChangeTo={setTo}
      onClearRange={handleClearRange}
      chartTitle={chartTitle}
      chartData={derived.chartData}
      colorOf={colorOf}
      legend={derived.legend}
      facets={facets}
      groupColumns={derived.groupColumns.length > 0 ? [...derived.groupColumns, '건수'] : ['건수']}
      groupRows={derived.groupRows}
      totalCount={derived.totalCount}
      loading={loading}
      error={error}
    />
  );
}
